import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarClock, Phone, Search, CheckCircle2, X, RotateCcw } from "lucide-react";

const STATUS_CLS = {
  scheduled: "bg-sky-100 text-sky-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-slate-200 text-slate-600",
};

export default function AdminVisitsPanel() {
  const [visits, setVisits] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/visits").then((r) => setVisits(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (v, status) => {
    try {
      await api.patch(`/visits/${v.id}/status`, { status });
      toast.success(`Visit marked ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const term = q.trim().toLowerCase();
  const filtered = visits.filter((v) => {
    if (filter !== "all" && v.status !== filter) return false;
    if (!term) return true;
    return [v.name, v.phone, v.hostel_name, v.hostel_city, v.owner_name].some((s) => (s || "").toLowerCase().includes(term));
  });

  return (
    <div data-testid="admin-visits-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="admin-visits-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, hostel…" className="pl-9" />
        </div>
        {["all", "scheduled", "completed", "cancelled"].map((s) => (
          <button
            key={s}
            data-testid={`admin-visits-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`text-[11px] font-bold rounded-full px-3 py-1.5 border capitalize ${filter === s ? "bg-orange-800 text-white border-orange-800" : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"}`}
          >
            {s} ({s === "all" ? visits.length : visits.filter((v) => v.status === s).length})
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {filtered.length === 0 && <div className="text-sm text-slate-500 bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center">No visits found.</div>}
        {filtered.map((v) => (
          <div key={v.id} data-testid={`admin-visit-${v.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                <CalendarClock size={14} className="text-orange-800" /> {v.name}
                <span className="text-xs text-slate-500 font-normal flex items-center gap-1"><Phone size={10} /> {v.phone}</span>
              </div>
              <div className="text-xs text-slate-600 mt-1">
                {v.hostel_name}, {v.hostel_city} · <b>{v.visit_date}</b> at <b>{v.visit_time}</b>
                {v.owner_name && <span className="text-slate-400"> · Owner: {v.owner_name}</span>}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">Requested {v.created_at ? new Date(v.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${STATUS_CLS[v.status] || "bg-slate-100 text-slate-700"}`}>{v.status}</span>
              {v.status === "scheduled" ? (
                <>
                  <button data-testid={`admin-visit-complete-${v.id}`} onClick={() => setStatus(v, "completed")} className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
                    <CheckCircle2 size={12} /> Completed
                  </button>
                  <button data-testid={`admin-visit-cancel-${v.id}`} onClick={() => setStatus(v, "cancelled")} className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50">
                    <X size={12} /> Cancel
                  </button>
                </>
              ) : (
                <button data-testid={`admin-visit-reschedule-${v.id}`} onClick={() => setStatus(v, "scheduled")} className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-3 py-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50">
                  <RotateCcw size={12} /> Re-open
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
